import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDownTrayIcon, ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { stripHtml as sanitizeStripHtml } from '../../utils/sanitize';
import { formatCalendarPartsInTimezone } from '../../utils/dateFormatter';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import {
  fetchAllKpiDrilldownTickets,
  fetchKpiDrilldownTickets,
  type KpiDrilldownTicketDto,
} from './kpiDashboardApi';
import { channelLabels } from '../dashboard/types/pulseReport';
import {
  KPI_DASHBOARD_TABLE_TD,
  KPI_DASHBOARD_TABLE_TD_PRIMARY,
  KPI_DASHBOARD_TABLE_TH,
  kpiDashboardTableRowClass,
} from './kpiDashboardTableStyles';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const TABLE_COL_COUNT = 23;

/** KPI tickets total scope (same filters; ticket types supplied by parent). */
const ALL_TICKETS_DRILL = { kind: 'metric' as const, key: 'tickets_total' };

const PRIORITY_P_LABEL: Record<string, string> = {
  critical_1: 'P1',
  high_2: 'P2',
  moderate_3: 'P3',
  low_4: 'P4',
  planning_5: 'P5',
};

const PRIORITY_TIER_LABEL: Record<string, string> = {
  critical_1: '1 - Critical',
  high_2: '2 - High',
  moderate_3: '3 - Moderate',
  low_4: '4 - Low',
  planning_5: '5 - Planning',
};

function priorityPLabel(priorityKey: string): string {
  return PRIORITY_P_LABEL[priorityKey] ?? '—';
}

function priorityTierLabel(priorityKey: string): string {
  return PRIORITY_TIER_LABEL[priorityKey] ?? '—';
}

function isSlaBreachedStatus(status: string): boolean {
  return status === 'BREACHED' || status === 'Breached';
}

function ticketHasAnySlaBreached(t: KpiDrilldownTicketDto): boolean {
  return (
    isSlaBreachedStatus(t.responseSla.status) ||
    isSlaBreachedStatus(t.ongoingSla.status) ||
    isSlaBreachedStatus(t.resolutionSla.status)
  );
}

function slaComplianceLabel(t: KpiDrilldownTicketDto): string {
  const parts = [t.responseSla, t.ongoingSla, t.resolutionSla];
  const applicable = parts.filter((p) => p.status && p.status !== 'N/A');
  if (applicable.length === 0) return '—';
  if (ticketHasAnySlaBreached(t)) return 'Non-compliant';
  return 'Compliant';
}

function internalAgingBucket(t: KpiDrilldownTicketDto): string {
  const endMs = t.ticketResolved
    ? new Date(t.ticketResolved).getTime()
    : t.ticketClosed
      ? new Date(t.ticketClosed).getTime()
      : Date.now();
  const startMs = new Date(t.ticketCreated).getTime();
  const hours = Math.max(0, (endMs - startMs) / 3600000);
  const days = hours / 24;
  if (hours <= 24) return '0-24 Hours';
  if (days <= 7) return '1-7 Days';
  if (days <= 30) return '8-30 Days';
  return '> 30 Days';
}

function formatDurationMins(m: number | null | undefined): string {
  if (m === null || m === undefined) return '—';
  return m.toLocaleString();
}

function ticketDetailPath(t: KpiDrilldownTicketDto): string {
  switch (t.ticketType) {
    case 'INCIDENT':
      return `/incidents/${t.id}`;
    case 'SERVICE_REQUEST':
      return `/service-requests/${t.id}`;
    case 'CASE':
      return `/cases/${t.id}`;
    case 'CHANGE':
      return `/changes/${t.id}`;
    case 'PROBLEM':
      return `/problems/${t.id}`;
    default:
      return `/tickets/${t.id}`;
  }
}

function openTicketRow(t: KpiDrilldownTicketDto) {
  window.open(ticketDetailPath(t), '_blank', 'noopener,noreferrer');
}

export interface KpiAllTicketsTabProps {
  filterValues: Record<string, string | string[]>;
  filterKey: string;
}

const KpiAllTicketsTab: React.FC<KpiAllTicketsTabProps> = ({ filterValues, filterKey }) => {
  const { formatDateTime: formatDateTimeBase, preferences } = useDateFormatter();
  const formatDateTime = useCallback(
    (value: string | null | undefined) => formatDateTimeBase(value),
    [formatDateTimeBase]
  );

  const timezoneOffset = useMemo(() => new Date().getTimezoneOffset(), []);
  const kpiTz = useMemo(
    () => ({ offsetMinutes: timezoneOffset, timeZone: preferences.timezone }),
    [timezoneOffset, preferences.timezone]
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [exportingCsv, setExportingCsv] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [filterKey, pageSize]);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['kpi-all-tickets', filterKey, page, pageSize, preferences.timezone],
    queryFn: () =>
      fetchKpiDrilldownTickets(filterValues, kpiTz, ALL_TICKETS_DRILL, page, pageSize),
    staleTime: 30_000,
  });

  const total = data?.total ?? 0;

  const handleExportCsv = useCallback(async () => {
    if (total === 0 || exportingCsv) return;
    setExportingCsv(true);
    try {
      const tickets = await fetchAllKpiDrilldownTickets(
        filterValues,
        kpiTz,
        ALL_TICKETS_DRILL,
        null
      );
      if (tickets.length === 0) {
        toast.error('No tickets to export for the current filters.');
        return;
      }
      const headers = [
        'Task',
        'Category',
        'Status',
        'Customer',
        'Customer Type',
        'Configuration Item',
        'Assignment Group',
        'Assigned To',
        'Contact Type',
        'Internal Aging Duration',
        'SLA Compliance',
        'Description',
        'Stage',
        'Has Breached',
        'Priority',
        'Priority Label',
        'Day',
        'Week of Year',
        'Month',
        'System Created On',
        'Start Time',
        'End Time',
        'Closed Time',
        'Duration (Minutes)',
      ];
      const rows = tickets.map((t) => {
        const desc = t.shortDescription ? sanitizeStripHtml(t.shortDescription).replace(/\s+/g, ' ').trim() : '';
        const cal = formatCalendarPartsInTimezone(t.ticketCreated, preferences.timezone);
        return [
          t.number,
          t.taskTypeLabel,
          t.stateLabel,
          t.company || '-',
          t.customerType || '-',
          t.resource || '-',
          t.assignmentGroup || '-',
          t.assignedTo || '-',
          t.channel ? channelLabels[t.channel] || t.channel : '-',
          internalAgingBucket(t),
          slaComplianceLabel(t),
          desc,
          t.stateLabel,
          ticketHasAnySlaBreached(t) ? 'True' : 'False',
          priorityTierLabel(t.priority),
          priorityPLabel(t.priority),
          cal.day,
          cal.week,
          cal.month,
          formatDateTime(t.ticketCreated),
          formatDateTime(t.responseSla.startTime),
          formatDateTime(t.resolutionSla.targetTime),
          formatDateTime(t.ticketClosed),
          formatDurationMins(t.ticketDurationMins),
        ];
      });
      const csvContent = [
        headers.join(','),
        ...rows.map((row) =>
          row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
        ),
      ].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kpi_all_tickets_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${tickets.length.toLocaleString()} row(s) to CSV.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'CSV export failed.');
    } finally {
      setExportingCsv(false);
    }
  }, [exportingCsv, filterValues, formatDateTime, kpiTz, preferences.timezone, total]);
  const totalPages = data?.totalPages ?? 0;

  const th = (extra = '') => `${KPI_DASHBOARD_TABLE_TH} ${extra}`.trim();
  const td = (extra = '') => `${KPI_DASHBOARD_TABLE_TD} ${extra}`.trim();

  return (
    <div className="relative flex min-h-0 flex-1 flex-col my-2 mx-5">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm shadow-slate-900/5 ring-1 ring-slate-950/[0.04]">
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-l-4 border-slate-100 border-l-indigo-500 bg-gradient-to-r from-slate-50 via-white to-indigo-50/40 py-1.5 pl-[13px] pr-2 sm:py-2 sm:pl-[17px] sm:pr-3">
          <h2 className="min-w-0 text-[11px] font-semibold uppercase tracking-wide text-slate-700 sm:text-xs">
            All Tickets
          </h2>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {isFetching ? (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600"
                aria-live="polite"
              >
                <ArrowPathIcon className="h-3 w-3 animate-spin" aria-hidden />
                Updating
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => void handleExportCsv()}
              disabled={total === 0 || exportingCsv || isLoading}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 sm:text-xs disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5 shrink-0 text-slate-600" aria-hidden />
              {exportingCsv ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>
        </div>

        {isLoading && (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-slate-500">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
            <p className="mt-3 text-sm">Loading tickets…</p>
          </div>
        )}

        {error && !isLoading && (
          <div className="flex shrink-0 items-start gap-2 border-b border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden />
            <span>{error instanceof Error ? error.message : 'Failed to load tickets.'}</span>
          </div>
        )}

        {data && !isLoading && (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="min-w-max w-full border-collapse text-xs sm:text-sm">
                <thead>
                  <tr>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Task
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Category
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Status
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Customer
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Configuration Item
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Assignment Group
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Assigned To
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Contact Type
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Internal Aging Duration
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      SLA Compliance
                    </th>
                    <th className={th('min-w-[260px] max-w-[480px]')} scope="col">
                      Description
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Stage
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Has Breached
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Priority
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Priority Label
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Day
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Week of Year
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Month
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      System Created On
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Start Time
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      End Time
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Closed Time
                    </th>
                    <th className={th('whitespace-nowrap')} scope="col">
                      Duration (Minutes)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.tickets.length === 0 ? (
                    <tr>
                      <td
                        colSpan={TABLE_COL_COUNT}
                        className="border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500"
                      >
                        No tickets match the current filters.
                      </td>
                    </tr>
                  ) : (
                    data.tickets.map((t, i) => {
                      const descRaw = t.shortDescription ? sanitizeStripHtml(t.shortDescription) : '';
                      const desc = descRaw.replace(/\s+/g, ' ').trim() || '—';
                      const cal = formatCalendarPartsInTimezone(t.ticketCreated, preferences.timezone);
                      return (
                        <tr
                          key={t.id}
                          className={`cursor-pointer ${kpiDashboardTableRowClass(i)}`}
                          onClick={() => openTicketRow(t)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openTicketRow(t);
                            }
                          }}
                          tabIndex={0}
                          role="link"
                          aria-label={`Open ticket ${t.number}`}
                        >
                          <td className={`${KPI_DASHBOARD_TABLE_TD_PRIMARY} whitespace-nowrap`}>{t.number}</td>
                          <td className={td('whitespace-nowrap')}>{t.taskTypeLabel}</td>
                          <td className={td('whitespace-nowrap')}>{t.stateLabel}</td>
                          <td className={td('max-w-[200px]')}>
                            <div className="truncate" title={t.company ?? undefined}>
                              {t.company || '—'}
                            </div>
                          </td>
                          <td className={td('max-w-[160px]')}>
                            <div className="truncate" title={t.resource ?? undefined}>
                              {t.resource || '—'}
                            </div>
                          </td>
                          <td className={td('whitespace-nowrap')}>{t.assignmentGroup || '—'}</td>
                          <td className={td('whitespace-nowrap')}>{t.assignedTo || '—'}</td>
                          <td className={td('whitespace-nowrap')}>
                            {t.channel ? channelLabels[t.channel] || t.channel : '—'}
                          </td>
                          <td className={td('whitespace-nowrap')}>{internalAgingBucket(t)}</td>
                          <td className={td('whitespace-nowrap')}>{slaComplianceLabel(t)}</td>
                          <td className={td('min-w-[260px] max-w-[480px] align-top')}>
                            <div className="line-clamp-3 break-words" title={desc}>
                              {desc}
                            </div>
                          </td>
                          <td className={td('whitespace-nowrap')}>{t.stateLabel}</td>
                          <td className={td('whitespace-nowrap')}>
                            {ticketHasAnySlaBreached(t) ? 'True' : 'False'}
                          </td>
                          <td className={td('whitespace-nowrap')}>{priorityTierLabel(t.priority)}</td>
                          <td className={td('whitespace-nowrap tabular-nums')}>{priorityPLabel(t.priority)}</td>
                          <td className={td('whitespace-nowrap')}>{cal.day}</td>
                          <td className={td('whitespace-nowrap')}>{cal.week}</td>
                          <td className={td('whitespace-nowrap')}>{cal.month}</td>
                          <td className={td('whitespace-nowrap')}>{formatDateTime(t.ticketCreated)}</td>
                          <td className={td('whitespace-nowrap')}>{formatDateTime(t.responseSla.startTime)}</td>
                          <td className={td('whitespace-nowrap')}>{formatDateTime(t.resolutionSla.targetTime)}</td>
                          <td className={td('whitespace-nowrap')}>{formatDateTime(t.ticketClosed)}</td>
                          <td className={td('whitespace-nowrap tabular-nums')}>
                            {formatDurationMins(t.ticketDurationMins)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {total > 0 ? (
              <div className="flex shrink-0 flex-col gap-2 border-t border-slate-200 bg-slate-50/50 px-2 py-2 sm:flex-row sm:items-center sm:justify-between sm:px-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-slate-700">Show</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                >
                  {PAGE_SIZE_OPTIONS.map((size, index) => {
                    const previousSize = index > 0 ? PAGE_SIZE_OPTIONS[index - 1]! : 0;
                    const isDisabled = previousSize >= total && size > total;
                    return (
                      <option key={size} value={size} disabled={isDisabled}>
                        {size}
                      </option>
                    );
                  })}
                </select>
                <span className="text-sm text-slate-700">entries</span>
              </div>
              <div className="text-center text-sm text-slate-700">
                {isFetching && !isLoading ? (
                  <span className="text-indigo-600">Updating…</span>
                ) : (
                  <>
                    Showing {Math.min((page - 1) * pageSize + 1, total)} to {Math.min(page * pageSize, total)} of{' '}
                    {total} entries
                  </>
                )}
              </div>
              <div className="flex flex-wrap items-center justify-center gap-1 sm:justify-end">
                <button
                  type="button"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  title="First page"
                >
                  &laquo;
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>
                {(() => {
                  const pages: (number | string)[] = [];
                  if (totalPages <= 7) {
                    for (let j = 1; j <= totalPages; j++) pages.push(j);
                  } else if (page <= 3) {
                    pages.push(1, 2, 3, 4, '...', totalPages);
                  } else if (page >= totalPages - 2) {
                    pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                  } else {
                    pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                  }
                  return pages.map((pNum, idx) =>
                    pNum === '...' ? (
                      <span key={`e-${idx}`} className="px-2 py-1 text-sm text-slate-500">
                        ...
                      </span>
                    ) : (
                      <button
                        key={pNum}
                        type="button"
                        onClick={() => setPage(pNum as number)}
                        className={`rounded-md border px-3 py-1 text-sm ${
                          page === pNum
                            ? 'border-indigo-600 bg-indigo-600 text-white'
                            : 'border-slate-300 bg-white hover:bg-slate-50'
                        }`}
                      >
                        {pNum}
                      </button>
                    )
                  );
                })()}
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
                <button
                  type="button"
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                  className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  title="Last page"
                >
                  &raquo;
                </button>
              </div>
            </div>
          ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default KpiAllTicketsTab;
